// Copyright 2025, University of Maryland, All Rights Reserved
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { HashRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from "@/components/ui/toaster"
import DebugIndicator from './components/DebugIndicator'
import { AppHeader, NavigationItem, AdminPage, ImpersonationBanner } from '@umd-dit-ai-workspace/workspaces-component'
import AccessDenied from './components/AccessDenied'
import WorkspaceBillsPage from './pages/WorkspaceBillsPage'
import Jobs from './pages/Jobs.tsx'
import ConfigPage from './pages/ConfigPage'
import Costs from './pages/Costs.tsx'
import DatasourceBillsPage from './pages/admin/DatasourceBillsPage'
import { AuthProvider, useAuth } from '@umd-dit-ai-workspace/workspaces-component'
import ProtectedAdminRoute from './components/ProtectedAdminRoute'
import { apiClient } from './api/api-client'
import { initializeJobsApi } from '@umd-dit-ai-workspace/jobs-component'
import { initializeWorkspacesApi } from '@umd-dit-ai-workspace/workspaces-component'
import { CASAuthProvider } from '@umd-dit-ai-workspace/cas-auth'
import "@/styles/globals.css"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5000,
      refetchOnWindowFocus: false,
      placeholderData: (previousData: unknown) => previousData,
      throwOnError: (error: Error) => {
        return error?.message?.includes('401') || error?.message?.includes('403')
      }
    },
    mutations: {
      retry: 1,
      retryDelay: 1000
    }
  }
})

function PreserveQueryParamsRoute() {
  const location = useLocation()
  return <Navigate to={`/bills${location.search}`} replace />
}

function Layout() {
  const { hasWorkspaceAccess } = useAuth();

  // Define navigation items specific to legislative-review
  // Only show navigation items if user has workspace access
  const navigationItems: NavigationItem[] = hasWorkspaceAccess ? [
    {
      path: '/bills',
      label: 'Bills'
    },
    {
      path: '/config',
      label: 'Config'
    },
    {
      path: '/admin/datasource',
      label: 'Datasources',
      adminOnly: true
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gray-100">
      <AppHeader
        appId="legislative-review"
        navigationItems={navigationItems}
        showDashboard={false}
        debugMode={import.meta.env.DEV || import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
      />

      {/* Impersonation banner - positioned below header, above main content */}
      <ImpersonationBanner app_id="legislative-review" />

      <main className="py-6">
        <div className="mx-auto px-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function WorkspaceAwareRouting() {
  const {
    isAuthenticated,
    isLoading,
    loadingWorkspaces,
    hasWorkspaceAccess
  } = useAuth();

  // If loading auth or workspaces, show loading
  if (isLoading || loadingWorkspaces) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-xl font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Render routes with security guards - Layout wraps all routes
  return (
    <Routes>
      <Route path="/" element={<Layout/>}>
        {/* If authenticated but no workspace access, show access denied within layout */}
        {isAuthenticated && !hasWorkspaceAccess ? (
          <Route index element={<AccessDenied />} />
        ) : (
          <Route index element={<PreserveQueryParamsRoute/>} />
        )}
        <Route path="bills" element={<WorkspaceBillsPage/>}/>
        <Route path="jobs" element={<Jobs/>}/>
        <Route path="costs" element={<Costs/>}/>
        <Route path="config" element={<ConfigPage/>}/>

        {/* Admin Section */}
        <Route path="admin" element={
          <ProtectedAdminRoute>
            <AdminPage
              app_id="legislative-review"
              appName="Legislative Review"
              debugMode={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
            />
          </ProtectedAdminRoute>
        }/>
        <Route path="admin/datasource" element={
          <ProtectedAdminRoute>
            <DatasourceBillsPage/>
          </ProtectedAdminRoute>
        }/>
      </Route>
      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Initialize Jobs API with our API client
    initializeJobsApi(apiClient.getAxiosInstance());

    // Initialize Workspaces API with our API client
    initializeWorkspacesApi(apiClient.getAxiosInstance());

    // Check for debug flag in URL on initial load
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      console.log('[App] Debug mode activated via URL parameter');
      sessionStorage.setItem('debugModeEnabled', 'true');
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  console.log('[App] CAS Configuration:', {
    casLoginUrl: import.meta.env.VITE_CAS_LOGIN_URL,
    serviceUrl: import.meta.env.VITE_CAS_SERVICE_URL,
    apiGatewayUrl: import.meta.env.VITE_CAS_API_URL,
  });

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <CASAuthProvider
          config={{
            casLoginUrl: import.meta.env.VITE_CAS_LOGIN_URL || 'https://login.umd.edu/cas/login',
            serviceUrl: import.meta.env.VITE_CAS_SERVICE_URL || window.location.origin,
            apiGatewayUrl: import.meta.env.VITE_CAS_API_URL || import.meta.env.VITE_API_URL + '/auth/cas/login',
            debug: import.meta.env.VITE_DEBUG_MODE === 'true',
          }}
          onAccessGranted={(authenticated: boolean, user) => {
            console.log('[App] CAS access granted:', { authenticated, user });
          }}
          onAccessDenied={() => {
            console.log('[App] CAS access denied');
          }}
        >
          <AuthProvider appName="legislative-review">
            <WorkspaceAwareRouting />
            <Toaster />
            <DebugIndicator />
          </AuthProvider>
        </CASAuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

// Debug log to check environment variables
console.log('[DEBUG] Environment Variables:', {
  apiUrl: import.meta.env.VITE_API_URL,
  debugMode: import.meta.env.VITE_DEBUG_MODE,
  mode: import.meta.env.MODE,
  base: import.meta.env.BASE_URL
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);